import { useTranslation } from 'react-i18next';
import type { LucideIcon } from 'lucide-react';
import { Check, Globe, Languages, Monitor, Moon, Palette, Sun } from 'lucide-react';
import { usePageTitle } from '@/components/layout/page-title';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from '@/components/ui/sonner';
import { cn } from '@/lib/utils';
import { useMe, useUpdatePreferences } from '@/features/auth/api';
import { CertificatesSection } from '@/features/certificates/CertificatesSection';
import { IssuerProfileSection } from '@/features/issuer/IssuerProfileSection';
import { DeleteAccountSection } from '@/features/settings/DeleteAccountSection';
import { SessionsSection } from '@/features/sessions/SessionsSection';
import { SettingsCard } from '@/features/settings/SettingsCard';
import { LANGUAGE_PREFERENCES, type LanguagePreference } from '@/i18n';
import { THEMES, type Theme } from '@/theme/ThemeProvider';

const THEME_ICON: Record<Theme, LucideIcon> = {
  light: Sun,
  dark: Moon,
  system: Monitor,
};

export function SettingsPage() {
  const { t } = useTranslation();
  const me = useMe();
  const updatePrefs = useUpdatePreferences();
  usePageTitle(t('settings.title'));

  const currentTheme: Theme = me.data?.preferences?.theme ?? 'system';
  const currentLanguage: LanguagePreference = me.data?.preferences?.language ?? 'system';

  const setTheme = (theme: Theme) => {
    if (theme === currentTheme) return;
    updatePrefs.mutate(
      { theme },
      {
        onError: () => toast.error(t('settings.save_failed')),
      },
    );
  };

  const setLanguage = (language: LanguagePreference) => {
    if (language === currentLanguage) return;
    updatePrefs.mutate(
      { language },
      {
        onError: () => toast.error(t('settings.save_failed')),
      },
    );
  };

  return (
    <div className="flex h-full flex-col">
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold tracking-tight sr-only md:not-sr-only">
          {t('settings.title')}
        </h1>
        <p className="text-sm text-muted-foreground max-md:hidden">{t('settings.subtitle')}</p>
      </header>

      {/* The header stays put; everything below scrolls inside its own custom-scrollbar area.
          The negative right margin parks the scrollbar in the page gutter, and pr-3 keeps the
          content's right edge aligned with the header above. */}
      <ScrollArea className="cn-vertical-scroll -mr-3 mt-6 min-h-0 flex-1 max-md:mt-0">
        {/* Bento grid over 6 columns: theme, language and certificates ride together as three
            compact boxes (2 cols each) on the top row; issuer profile and sessions sit side by
            side (3 cols each) below, together filling the width; the danger zone spans full width.
            Collapses to a single column below md. */}
        <div className="grid gap-4 pr-3 md:grid-cols-6">
          <SettingsCard
            className="md:col-span-2"
            icon={Palette}
            title={t('settings.theme')}
            description={t('settings.theme_hint')}
          >
            <OptionRows
              ariaLabel={t('settings.theme')}
              value={currentTheme}
              disabled={updatePrefs.isPending}
              options={THEMES.map((value) => ({
                value,
                label: t(`theme.${value}`),
                icon: THEME_ICON[value],
              }))}
              onChange={setTheme}
            />
          </SettingsCard>

          <SettingsCard
            className="md:col-span-2"
            icon={Languages}
            title={t('settings.language')}
            description={t('settings.language_hint')}
          >
            <OptionRows
              ariaLabel={t('settings.language')}
              value={currentLanguage}
              disabled={updatePrefs.isPending}
              options={LANGUAGE_PREFERENCES.map((value) => ({
                value,
                label:
                  value === 'en'
                    ? t('common.english')
                    : value === 'pl'
                      ? t('common.polish')
                      : t('settings.language_system'),
                icon: value === 'system' ? Globe : undefined,
                code: value === 'en' ? 'EN' : value === 'pl' ? 'PL' : undefined,
              }))}
              onChange={setLanguage}
            />
          </SettingsCard>

          <CertificatesSection className="md:col-span-2" />

          <IssuerProfileSection className="md:col-span-3" />

          <SessionsSection className="md:col-span-3" />

          <DeleteAccountSection className="md:col-span-6" />
        </div>
      </ScrollArea>
    </div>
  );
}

interface OptionRow<T extends string> {
  value: T;
  label: string;
  /** Leading glyph; falls back to `code` when absent. */
  icon?: LucideIcon;
  /** Short text glyph (e.g. a language code) used when there's no icon. */
  code?: string;
}

interface OptionRowsProps<T extends string> {
  value: T;
  options: ReadonlyArray<OptionRow<T>>;
  onChange: (value: T) => void;
  ariaLabel: string;
  disabled?: boolean;
}

/**
 * A segmented picker rendered as a vertical list of rows — leading glyph, label, and a trailing
 * check on the active row. The glyph is decorative (aria-hidden) so each row's accessible name is
 * just its label.
 */
function OptionRows<T extends string>({
  value,
  options,
  onChange,
  ariaLabel,
  disabled,
}: OptionRowsProps<T>) {
  return (
    <div role="radiogroup" aria-label={ariaLabel} className="flex flex-col gap-2">
      {options.map((opt) => {
        const active = opt.value === value;
        const Icon = opt.icon;
        return (
          <button
            key={opt.value}
            type="button"
            role="radio"
            aria-checked={active}
            aria-disabled={disabled || undefined}
            onClick={() => {
              if (disabled) return;
              onChange(opt.value);
            }}
            className={cn(
              'flex cursor-pointer items-center gap-3 rounded-lg border px-3 py-2.5 text-sm font-medium transition-colors outline-none',
              'focus-visible:ring-2 focus-visible:ring-ring/60',
              active
                ? 'border-primary/40 bg-accent text-foreground ring-1 ring-primary/30'
                : 'border-border text-muted-foreground hover:border-foreground/20 hover:bg-accent/50 hover:text-foreground',
              disabled && 'pointer-events-none opacity-60',
            )}
          >
            <span
              aria-hidden
              className={cn(
                'grid size-8 shrink-0 place-items-center rounded-md border text-[11px] font-semibold tracking-wide',
                active
                  ? 'border-primary/30 bg-background text-foreground'
                  : 'border-border bg-muted/40',
              )}
            >
              {Icon ? <Icon className="size-4" /> : opt.code}
            </span>
            <span className="flex-1 text-left">{opt.label}</span>
            {active ? (
              <Check className="size-4 shrink-0 text-primary" aria-hidden />
            ) : null}
          </button>
        );
      })}
    </div>
  );
}
